/**
 * LandingPageEditor - Comprehensive landing page customization
 * Features: Hero, Features, Testimonials, Partners, FAQ sections
 */
import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MediaUpload } from '@/components/MediaUpload';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { toast } from 'sonner';
import {
  Layout, Palette, Image, Video, Save, Loader2, Plus, Trash2, GripVertical,
  Upload, X, Eye, Star, Users, MessageSquare, HelpCircle, Building2, Quote,
  Ticket, QrCode, Coins, Shield, MessageCircle, ChevronUp, ChevronDown,
  ExternalLink, Facebook, Twitter, Instagram, Linkedin, Youtube, Globe
} from 'lucide-react';
import api, { systemAPI, getErrorMessage } from '@/lib/api';
import { useAuthStore } from '@/lib/store';
import VideoOutputPicker from '@/components/broadcast/VideoOutputPicker';

// Icon options for features
const ICON_OPTIONS = [
  { value: 'Ticket', label: 'Ticket' },
  { value: 'QrCode', label: 'QR Code' },
  { value: 'Coins', label: 'Coins' },
  { value: 'Users', label: 'Users' },
  { value: 'Shield', label: 'Shield' },
  { value: 'MessageCircle', label: 'Message' },
  { value: 'Star', label: 'Star' },
  { value: 'Building2', label: 'Building' },
  { value: 'Globe', label: 'Globe' },
  { value: 'Calendar', label: 'Calendar' },
  { value: 'CreditCard', label: 'Credit Card' },
  { value: 'Video', label: 'Video' },
  { value: 'Mail', label: 'Email' },
  { value: 'Phone', label: 'Phone' },
  { value: 'MapPin', label: 'Location' },
  { value: 'Clock', label: 'Clock' },
];


// Feature Item Editor
const FeatureItemEditor = ({ feature, index, onChange, onRemove, onMove, isFirst, isLast }) => (
  <div className="p-4 border rounded-lg bg-slate-50 space-y-3">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <GripVertical className="h-4 w-4 text-slate-400" />
        <span className="font-medium text-sm">Feature {index + 1}</span>
      </div>
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="icon" onClick={() => onMove('up')} disabled={isFirst} className="h-7 w-7">
          <ChevronUp className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" onClick={() => onMove('down')} disabled={isLast} className="h-7 w-7">
          <ChevronDown className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" onClick={onRemove} className="h-7 w-7 text-red-500 hover:text-red-700">
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      <div>
        <Label className="text-xs">Icon</Label>
        <Select value={feature.icon} onValueChange={(v) => onChange({ ...feature, icon: v })}>
          <SelectTrigger className="h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ICON_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-2">
        <Label className="text-xs">Title</Label>
        <Input
          value={feature.title}
          onChange={(e) => onChange({ ...feature, title: e.target.value })}
          placeholder="Feature title"
          className="h-9"
        />
      </div>
    </div>
    <div>
      <Label className="text-xs">Description</Label>
      <Input
        value={feature.description}
        onChange={(e) => onChange({ ...feature, description: e.target.value })}
        placeholder="Short description"
        className="h-9"
      />
    </div>
  </div>
);

// Testimonial Item Editor
const TestimonialItemEditor = ({ item, index, onChange, onRemove }) => (
  <div className="p-4 border rounded-lg bg-slate-50 space-y-3">
    <div className="flex items-center justify-between">
      <span className="font-medium text-sm flex items-center gap-2">
        <Quote className="h-4 w-4 text-indigo-500" />
        Testimonial {index + 1}
      </span>
      <Button variant="ghost" size="icon" onClick={onRemove} className="h-7 w-7 text-red-500">
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label className="text-xs">Name</Label>
        <Input
          value={item.name || ''}
          onChange={(e) => onChange({ ...item, name: e.target.value })}
          placeholder="John Doe"
          className="h-9"
        />
      </div>
      <div>
        <Label className="text-xs">Title/Company</Label>
        <Input
          value={item.title || ''}
          onChange={(e) => onChange({ ...item, title: e.target.value })}
          placeholder="CEO at Company"
          className="h-9"
        />
      </div>
    </div>
    <div>
      <Label className="text-xs">Quote</Label>
      <Textarea
        value={item.quote || ''}
        onChange={(e) => onChange({ ...item, quote: e.target.value })}
        placeholder="What they said about your service..."
        className="min-h-[60px]"
      />
    </div>
    <div>
      <MediaUpload
        value={item.avatar || ''}
        onChange={(url) => onChange({ ...item, avatar: url })}
        purpose="avatar"
        label="Avatar (optional)"
        testId={`testimonial-avatar-upload-${index}`}
        previewClassName="h-16"
      />
    </div>
  </div>
);

// Partner Item Editor
const PartnerItemEditor = ({ item, index, onChange, onRemove }) => (
  <div className="p-3 border rounded-lg bg-slate-50 flex items-center gap-3">
    <div className="flex-1 grid grid-cols-2 gap-2">
      <Input
        value={item.name || ''}
        onChange={(e) => onChange({ ...item, name: e.target.value })}
        placeholder="Company name"
        className="h-9"
      />
      <MediaUpload
        value={item.logo || ''}
        onChange={(url) => onChange({ ...item, logo: url })}
        purpose="logo"
        label="Logo"
        testId={`partner-logo-upload-${index}`}
        previewClassName="h-12"
      />
    </div>
    <Button variant="ghost" size="icon" onClick={onRemove} className="h-8 w-8 text-red-500">
      <Trash2 className="h-4 w-4" />
    </Button>
  </div>
);

// FAQ Item Editor
const FAQItemEditor = ({ item, index, onChange, onRemove }) => (
  <div className="p-4 border rounded-lg bg-slate-50 space-y-3">
    <div className="flex items-center justify-between">
      <span className="font-medium text-sm flex items-center gap-2">
        <HelpCircle className="h-4 w-4 text-amber-500" />
        Question {index + 1}
      </span>
      <Button variant="ghost" size="icon" onClick={onRemove} className="h-7 w-7 text-red-500">
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
    <div>
      <Label className="text-xs">Question</Label>
      <Input
        value={item.question || ''}
        onChange={(e) => onChange({ ...item, question: e.target.value })}
        placeholder="What is your question?"
        className="h-9"
      />
    </div>
    <div>
      <Label className="text-xs">Answer</Label>
      <Textarea
        value={item.answer || ''}
        onChange={(e) => onChange({ ...item, answer: e.target.value })}
        placeholder="The answer to the question..."
        className="min-h-[60px]"
      />
    </div>
  </div>
);

const LandingPageEditor = () => {
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState({
    // Hero settings
    hero_type: 'gradient',
    hero_gradient_from: '#3B82F6',
    hero_gradient_to: '#6366F1',
    hero_color: '#3B82F6',
    hero_image_url: '',
    hero_video_url: '',
    hero_overlay_opacity: 20,
    hero_title: 'EventFlow',
    hero_subtitle: 'AI-Native Event Management Platform',
    hero_text_color: '#FFFFFF',
    hero_cta_text: 'Get Started',
    hero_cta_link: '/login',
    // Features section
    features_enabled: true,
    features_title: 'Complete Event Lifecycle Management',
    features: [
      { icon: 'Ticket', title: 'Smart Registration', description: 'Multi-step funnel with real-time inventory' },
      { icon: 'QrCode', title: 'Digital Badges', description: 'Secure QR codes for seamless check-in' },
      { icon: 'Coins', title: 'Event Economy', description: 'Internal coin system for engagement' },
      { icon: 'Users', title: 'AI Networking', description: 'Smart matchmaking and icebreakers' },
      { icon: 'Shield', title: 'Security Center', description: 'Gate control and incident management' },
      { icon: 'MessageCircle', title: 'AI Concierge', description: 'Instant answers to attendee questions' }
    ],
    // Testimonials section
    testimonials_enabled: false,
    testimonials_title: 'What Our Clients Say',
    testimonials: [],
    // Partners section
    partners_enabled: false,
    partners_title: 'Trusted By',
    partners: [],
    // FAQ section
    faq_enabled: false,
    faq_title: 'Frequently Asked Questions',
    faq_items: [],
    // Footer
    footer_text: '',
    social_links: {}
  });

  // Fetch settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ['system-settings'],
    queryFn: () => systemAPI.getSettings().then(r => r.data),
  });

  // Update form when settings load
  useEffect(() => {
    if (settings?.landing) {
      setFormData(prev => ({ ...prev, ...settings.landing }));
    }
  }, [settings]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data) => systemAPI.updateSettings('landing', data),
    onSuccess: () => {
      toast.success('Landing page settings saved!');
      queryClient.invalidateQueries(['system-settings']);
      queryClient.invalidateQueries(['app-config']);
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to save'))
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    saveMutation.mutate(formData);
  };

  // Array manipulation helpers
  const addFeature = () => {
    handleChange('features', [...formData.features, { icon: 'Star', title: '', description: '' }]);
  };

  const updateFeature = (index, feature) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = feature;
    handleChange('features', newFeatures);
  };

  const removeFeature = (index) => {
    handleChange('features', formData.features.filter((_, i) => i !== index));
  };

  const moveFeature = (index, direction) => {
    const newFeatures = [...formData.features];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [newFeatures[index], newFeatures[newIndex]] = [newFeatures[newIndex], newFeatures[index]];
    handleChange('features', newFeatures);
  };

  const addTestimonial = () => {
    handleChange('testimonials', [...formData.testimonials, { name: '', title: '', quote: '', avatar: '' }]);
  };

  const updateTestimonial = (index, item) => {
    const newItems = [...formData.testimonials];
    newItems[index] = item;
    handleChange('testimonials', newItems);
  };

  const removeTestimonial = (index) => {
    handleChange('testimonials', formData.testimonials.filter((_, i) => i !== index));
  };

  const addPartner = () => {
    handleChange('partners', [...formData.partners, { name: '', logo: '' }]);
  };

  const updatePartner = (index, item) => {
    const newItems = [...formData.partners];
    newItems[index] = item;
    handleChange('partners', newItems);
  };

  const removePartner = (index) => {
    handleChange('partners', formData.partners.filter((_, i) => i !== index));
  };

  const addFAQ = () => {
    handleChange('faq_items', [...formData.faq_items, { question: '', answer: '' }]);
  };

  const updateFAQ = (index, item) => {
    const newItems = [...formData.faq_items];
    newItems[index] = item;
    handleChange('faq_items', newItems);
  };

  const removeFAQ = (index) => {
    handleChange('faq_items', formData.faq_items.filter((_, i) => i !== index));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="landing-page-editor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Layout className="h-6 w-6 text-indigo-600" />
            Landing Page Editor
          </h2>
          <p className="text-slate-500 text-sm">Customize your public landing page</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => window.open('/', '_blank')}>
            <Eye className="h-4 w-4 mr-2" />
            Preview
          </Button>
          <Button onClick={handleSave} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      <Tabs defaultValue="hero" className="space-y-4">
        <TabsList className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 w-full">
          <TabsTrigger value="hero" className="flex items-center gap-1">
            <Palette className="h-4 w-4" />
            <span className="hidden sm:inline">Hero</span>
          </TabsTrigger>
          <TabsTrigger value="features" className="flex items-center gap-1">
            <Star className="h-4 w-4" />
            <span className="hidden sm:inline">Features</span>
          </TabsTrigger>
          <TabsTrigger value="testimonials" className="flex items-center gap-1">
            <Quote className="h-4 w-4" />
            <span className="hidden sm:inline">Testimonials</span>
          </TabsTrigger>
          <TabsTrigger value="partners" className="flex items-center gap-1">
            <Building2 className="h-4 w-4" />
            <span className="hidden sm:inline">Partners</span>
          </TabsTrigger>
          <TabsTrigger value="faq" className="flex items-center gap-1">
            <HelpCircle className="h-4 w-4" />
            <span className="hidden sm:inline">FAQ</span>
          </TabsTrigger>
        </TabsList>

        {/* Hero Section */}
        <TabsContent value="hero">
          <Card>
            <CardHeader>
              <CardTitle>Hero Section</CardTitle>
              <CardDescription>The main banner at the top of your landing page</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Hero Type */}
              <div className="space-y-3">
                <Label className="font-semibold">Background Type</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { value: 'gradient', label: 'Gradient', icon: Palette },
                    { value: 'color', label: 'Solid Color', icon: Palette },
                    { value: 'image', label: 'Image', icon: Image },
                    { value: 'video', label: 'Video', icon: Video },
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleChange('hero_type', option.value)}
                      className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2
                        ${formData.hero_type === option.value 
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                          : 'border-slate-200 hover:border-slate-300'}`}
                    >
                      <option.icon className="h-6 w-6" />
                      <span className="text-sm font-medium">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Type-specific settings */}
              {formData.hero_type === 'gradient' && (
                <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div className="space-y-2">
                    <Label>From Color</Label>
                    <div className="flex gap-2">
                      <input type="color" value={formData.hero_gradient_from} onChange={(e) => handleChange('hero_gradient_from', e.target.value)} className="h-10 w-14 rounded border cursor-pointer" />
                      <Input value={formData.hero_gradient_from} onChange={(e) => handleChange('hero_gradient_from', e.target.value)} className="flex-1" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>To Color</Label>
                    <div className="flex gap-2">
                      <input type="color" value={formData.hero_gradient_to} onChange={(e) => handleChange('hero_gradient_to', e.target.value)} className="h-10 w-14 rounded border cursor-pointer" />
                      <Input value={formData.hero_gradient_to} onChange={(e) => handleChange('hero_gradient_to', e.target.value)} className="flex-1" />
                    </div>
                  </div>
                </div>
              )}

              {formData.hero_type === 'color' && (
                <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                  <Label>Background Color</Label>
                  <div className="flex gap-2">
                    <input type="color" value={formData.hero_color} onChange={(e) => handleChange('hero_color', e.target.value)} className="h-10 w-14 rounded border cursor-pointer" />
                    <Input value={formData.hero_color} onChange={(e) => handleChange('hero_color', e.target.value)} className="flex-1 max-w-xs" />
                  </div>
                </div>
              )}

              {formData.hero_type === 'image' && (
                <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                  <MediaUpload
                    label="Hero image"
                    purpose="hero"
                    testId="landing-hero-image"
                    value={formData.hero_image_url}
                    onChange={(url) => handleChange('hero_image_url', url)}
                  />
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <Label>Overlay Darkness</Label>
                      <span className="text-sm text-slate-500">{formData.hero_overlay_opacity}%</span>
                    </div>
                    <input type="range" min="0" max="80" value={formData.hero_overlay_opacity} onChange={(e) => handleChange('hero_overlay_opacity', parseInt(e.target.value))} className="w-full" />
                  </div>
                </div>
              )}

              {formData.hero_type === 'video' && (
                <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                  <div className="space-y-2">
                    <Label>Hero video</Label>
                    <MediaUpload
                      value={formData.hero_video_url}
                      purpose="video"
                      testId="landing-hero-video"
                      onChange={(url) => handleChange('hero_video_url', url)}
                    />
                    <VideoOutputPicker
                      tenantId={useAuthStore.getState().user?.tenant_id || 'global'}
                      label="Or pick a rendered clip from the Video Library"
                      testIdPrefix="landing-hero-video-picker"
                      onPick={(url) => handleChange('hero_video_url', url)}
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <Label>Overlay Darkness</Label>
                      <span className="text-sm text-slate-500">{formData.hero_overlay_opacity}%</span>
                    </div>
                    <input type="range" min="0" max="80" value={formData.hero_overlay_opacity} onChange={(e) => handleChange('hero_overlay_opacity', parseInt(e.target.value))} className="w-full" />
                  </div>
                </div>
              )}

              {/* Hero Text */}
              <div className="space-y-4">
                <Label className="font-semibold">Hero Content</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm">Title</Label>
                    <Input value={formData.hero_title} onChange={(e) => handleChange('hero_title', e.target.value)} placeholder="Your App Name" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Subtitle</Label>
                    <Input value={formData.hero_subtitle} onChange={(e) => handleChange('hero_subtitle', e.target.value)} placeholder="Your tagline here" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm">CTA Button Text</Label>
                    <Input value={formData.hero_cta_text} onChange={(e) => handleChange('hero_cta_text', e.target.value)} placeholder="Get Started" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">CTA Link</Label>
                    <Input value={formData.hero_cta_link} onChange={(e) => handleChange('hero_cta_link', e.target.value)} placeholder="/login" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Text Color</Label>
                    <div className="flex gap-2">
                      <input type="color" value={formData.hero_text_color} onChange={(e) => handleChange('hero_text_color', e.target.value)} className="h-9 w-14 rounded border cursor-pointer" />
                      <Input value={formData.hero_text_color} onChange={(e) => handleChange('hero_text_color', e.target.value)} className="flex-1" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Live Preview */}
              <div className="space-y-2">
                <Label className="font-semibold">Preview</Label>
                <div
                  className="relative overflow-hidden rounded-lg h-48"
                  style={{
                    ...(formData.hero_type === 'gradient' && {
                      background: `linear-gradient(to right, ${formData.hero_gradient_from}, ${formData.hero_gradient_to})`
                    }),
                    ...(formData.hero_type === 'color' && {
                      backgroundColor: formData.hero_color
                    }),
                    ...(formData.hero_type === 'image' && formData.hero_image_url && {
                      backgroundImage: `url(${formData.hero_image_url})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }),
                    ...(!formData.hero_image_url && formData.hero_type === 'image' && {
                      background: `linear-gradient(to right, #3B82F6, #6366F1)`
                    })
                  }}
                >
                  {formData.hero_type === 'video' && formData.hero_video_url && (
                    <video src={formData.hero_video_url} className="absolute inset-0 w-full h-full object-cover" autoPlay muted loop playsInline />
                  )}
                  <div className="absolute inset-0" style={{ backgroundColor: `rgba(0,0,0,${formData.hero_overlay_opacity / 100})` }} />
                  <div className="relative h-full flex flex-col items-center justify-center text-center p-6">
                    <h1 className="text-3xl font-bold" style={{ color: formData.hero_text_color }}>{formData.hero_title || 'Your Title'}</h1>
                    <p className="mt-2 text-lg opacity-90" style={{ color: formData.hero_text_color }}>{formData.hero_subtitle || 'Your subtitle'}</p>
                    <Button className="mt-4" variant="secondary">{formData.hero_cta_text || 'Get Started'}</Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Features Section */}
        <TabsContent value="features">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Features Section</CardTitle>
                  <CardDescription>Highlight your platform's key features</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <Label className="text-sm">Show Section</Label>
                  <Switch checked={formData.features_enabled} onCheckedChange={(v) => handleChange('features_enabled', v)} />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Section Title</Label>
                <Input value={formData.features_title} onChange={(e) => handleChange('features_title', e.target.value)} placeholder="Complete Event Lifecycle Management" />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="font-semibold">Features ({formData.features.length})</Label>
                  <Button size="sm" variant="outline" onClick={addFeature}>
                    <Plus className="h-4 w-4 mr-1" /> Add Feature
                  </Button>
                </div>
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {formData.features.map((feature, index) => (
                    <FeatureItemEditor
                      key={index}
                      feature={feature}
                      index={index}
                      onChange={(f) => updateFeature(index, f)}
                      onRemove={() => removeFeature(index)}
                      onMove={(dir) => moveFeature(index, dir)}
                      isFirst={index === 0}
                      isLast={index === formData.features.length - 1}
                    />
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Testimonials Section */}
        <TabsContent value="testimonials">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Testimonials</CardTitle>
                  <CardDescription>Showcase client feedback and reviews</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <Label className="text-sm">Show Section</Label>
                  <Switch checked={formData.testimonials_enabled} onCheckedChange={(v) => handleChange('testimonials_enabled', v)} />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Section Title</Label>
                <Input value={formData.testimonials_title} onChange={(e) => handleChange('testimonials_title', e.target.value)} placeholder="What Our Clients Say" />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="font-semibold">Testimonials ({formData.testimonials.length})</Label>
                  <Button size="sm" variant="outline" onClick={addTestimonial}>
                    <Plus className="h-4 w-4 mr-1" /> Add Testimonial
                  </Button>
                </div>
                {formData.testimonials.length === 0 ? (
                  <div className="text-center py-8 text-slate-500 border-2 border-dashed rounded-lg">
                    <Quote className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                    <p>No testimonials yet. Add one to showcase client feedback.</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {formData.testimonials.map((item, index) => (
                      <TestimonialItemEditor
                        key={index}
                        item={item}
                        index={index}
                        onChange={(t) => updateTestimonial(index, t)}
                        onRemove={() => removeTestimonial(index)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Partners Section */}
        <TabsContent value="partners">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Partners & Clients</CardTitle>
                  <CardDescription>Display trusted partner logos</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <Label className="text-sm">Show Section</Label>
                  <Switch checked={formData.partners_enabled} onCheckedChange={(v) => handleChange('partners_enabled', v)} />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Section Title</Label>
                <Input value={formData.partners_title} onChange={(e) => handleChange('partners_title', e.target.value)} placeholder="Trusted By" />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="font-semibold">Partners ({formData.partners.length})</Label>
                  <Button size="sm" variant="outline" onClick={addPartner}>
                    <Plus className="h-4 w-4 mr-1" /> Add Partner
                  </Button>
                </div>
                {formData.partners.length === 0 ? (
                  <div className="text-center py-8 text-slate-500 border-2 border-dashed rounded-lg">
                    <Building2 className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                    <p>No partners yet. Add logos to build trust.</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {formData.partners.map((item, index) => (
                      <PartnerItemEditor
                        key={index}
                        item={item}
                        index={index}
                        onChange={(p) => updatePartner(index, p)}
                        onRemove={() => removePartner(index)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* FAQ Section */}
        <TabsContent value="faq">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>FAQ Section</CardTitle>
                  <CardDescription>Answer common questions</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <Label className="text-sm">Show Section</Label>
                  <Switch checked={formData.faq_enabled} onCheckedChange={(v) => handleChange('faq_enabled', v)} />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Section Title</Label>
                <Input value={formData.faq_title} onChange={(e) => handleChange('faq_title', e.target.value)} placeholder="Frequently Asked Questions" />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="font-semibold">Questions ({formData.faq_items.length})</Label>
                  <Button size="sm" variant="outline" onClick={addFAQ}>
                    <Plus className="h-4 w-4 mr-1" /> Add Question
                  </Button>
                </div>
                {formData.faq_items.length === 0 ? (
                  <div className="text-center py-8 text-slate-500 border-2 border-dashed rounded-lg">
                    <HelpCircle className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                    <p>No FAQs yet. Add common questions and answers.</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {formData.faq_items.map((item, index) => (
                      <FAQItemEditor
                        key={index}
                        item={item}
                        index={index}
                        onChange={(f) => updateFAQ(index, f)}
                        onRemove={() => removeFAQ(index)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Fixed Save Button */}
      <div className="sticky bottom-4 flex justify-end">
        <Button size="lg" onClick={handleSave} disabled={saveMutation.isPending} className="shadow-lg">
          {saveMutation.isPending ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Save className="h-5 w-5 mr-2" />}
          Save All Changes
        </Button>
      </div>
    </div>
  );
};

export default LandingPageEditor;
